import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte,
  VpbBerekening
} from '@snelstart-ventures/fiscaal/domain-logic';
import {AbstractControl, FormArray, FormControl, FormGroup} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SpecKerComponent} from '../spec-ker/spec-ker.component';
import {SpecHir2Component} from '../spec-hir-2/spec-hir-2.component';
import {SpecGarantievoorzieningComponent} from '../spec-garantievoorziening/spec-garantievoorziening.component';
import {SpecOverigeVoorzieningComponent} from '../spec-overige-voorziening/spec-overige-voorziening.component';
import {SpecOmzetbelastingPassiefComponent} from '../spec-omzetbelasting-passief/spec-omzetbelasting-passief.component';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-balans-passiva',
    templateUrl: './balans-passiva.component.html',
    styleUrls: ['./balans-passiva.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        SpecKerComponent,
        SpecHir2Component,
        SpecGarantievoorzieningComponent,
        SpecOverigeVoorzieningComponent,
        SpecOmzetbelastingPassiefComponent,
        CommonModule
    ]
})
export class BalansPassivaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public isFE = false;
  public dialogVisible = false;
  public labelBalansOpening = '';
  public labelBalansSlot = '';

  public balansEnResultatenRekeningFg: FormGroup = this.dataService.createBalansEnResultatenRekeningFormGroup();

  public berekendePassivaFg = new FormGroup({
    totaalPassivaBegin: new FormControl<number | null>(0),
    totaalPassivaEind: new FormControl<number | null>(0)
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) {
          return;
        }

        const begindatum = data.aangifte.boekjaar?.begindatum;
        const slotdatum = data.aangifte.boekjaar?.einddatum;

        this.labelBalansOpening = begindatum
          ? `Balans ${new Date(begindatum).toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'})}`
          : 'Openingsbalans';
        this.labelBalansSlot = slotdatum
          ? `Balans ${new Date(slotdatum).toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'})}`
          : 'Slotbalans';

        this.isFE = data.aangifte.identificatie.aangifteFiscaleEenheid;
        this.syncFeDisabledState();
        this.balansEnResultatenRekeningFg.patchValue(data.aangifte.balansEnResultatenrekening, {emitEvent: false});
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening$.pipe(
      filter((data): data is VpbBerekening => !!data),
      tap(vpbBerekening => {
        const passiva = vpbBerekening.jaarstukken?.passiva;
        if (passiva) {
          this.berekendePassivaFg.patchValue(passiva, {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.balansEnResultatenRekeningFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
      this.dataService.calculateLatestCorporateTaxReturn().subscribe();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
  }

  private readonly feDisabledControlNames = new Set(['openingsbalans', 'slotbalans']);

  private setFeDisabledState(control: AbstractControl, disabled: boolean): void {
    if (control instanceof FormArray) {
      control.controls.forEach(child => this.setFeDisabledState(child, disabled));
      return;
    }

    if (control instanceof FormGroup) {
      Object.entries(control.controls).forEach(([name, child]) => {
        if (child instanceof FormControl) {
          if (!this.feDisabledControlNames.has(name)) {
            return;
          }

          if (disabled) {
            child.disable({emitEvent: false});
          } else {
            child.enable({emitEvent: false});
          }
          return;
        }

        this.setFeDisabledState(child, disabled);
      });
    }
  }

  private syncFeDisabledState(): void {
    this.setFeDisabledState(this.balansEnResultatenRekeningFg, this.isFE);
  }

  getArrayControls(arrayName: string): AbstractControl[] {
    return (this.balansEnResultatenRekeningFg.get(arrayName) as FormArray).controls;
  }

  saveForm(): void {
    this.balansEnResultatenRekeningFg.updateValueAndValidity({emitEvent: false});
    const balansEnResultatenrekening = this.balansEnResultatenRekeningFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening,
        } as VpbAangifte,
      } as CorporateTaxEntity;
    });
  }
}
